// Client portal access matrix (P1b-PORTAL2): what each client role sees and may do in the portal, with per-client overrides.
// The matrix is one workspace setting, `clientPortalAccess` ({roles, clients}). A flag that is not saved is on, so a
// workspace with no saved matrix keeps the portal it had. The server enforces the matrix on every API call a client makes
// (apps/server/src/client-portal-access.ts); the portal shell hides what is off. Neither side trusts the other.
import { z } from "zod";

/** The built-in client roles the matrix has columns for (packages/authz/src/defaults.ts). */
export const CLIENT_ACCESS_ROLE_IDS = ["role-client-member", "role-client-viewer", "role-client-billing", "role-client-guest"] as const;

/** Sidebar destinations of the client portal. Each one is a row of the matrix. */
export const CLIENT_ACCESS_SECTIONS = ["home", "projects", "approvals", "requests", "changes", "briefs", "social", "money", "plans", "store", "messages", "tickets", "calendar", "files", "reports", "forms", "help", "apps"] as const;

/** Actions the portal offers, each a row of the matrix under the sections. */
export const CLIENT_ACCESS_ACTIONS = ["createTickets", "uploadFiles", "seeInvoices", "seeTimeLogs", "seeReports"] as const;

export const CLIENT_ACCESS_KEYS = [...CLIENT_ACCESS_SECTIONS, ...CLIENT_ACCESS_ACTIONS] as const;
export type ClientAccessKey = (typeof CLIENT_ACCESS_KEYS)[number];

const FLAGS = z.record(z.string().max(40), z.boolean());

/** The saved matrix. `roles` is keyed by role id, `clients` by client id; each holds a flag per key. */
export const CLIENT_PORTAL_ACCESS = z.object({
  roles: z.record(z.string().max(80), FLAGS).default({}),
  clients: z.record(z.string().max(80), FLAGS).default({}),
});
export type ClientPortalAccess = z.infer<typeof CLIENT_PORTAL_ACCESS>;

export const CLIENT_PORTAL_ACCESS_DEFAULT: ClientPortalAccess = { roles: {}, clients: {} };

/** Whether one key is on for a client contact. A client override wins, then the role's flag, then on (today's portal). */
export function clientKeyOn(access: ClientPortalAccess | null | undefined, roleId: string | null, clientId: string | null, key: string): boolean {
  const own = clientId ? access?.clients?.[clientId]?.[key] : undefined;
  if (typeof own === "boolean") return own;
  const role = roleId ? access?.roles?.[roleId]?.[key] : undefined;
  if (typeof role === "boolean") return role;
  return true;
}

/** Every key of the matrix for one client contact, as the portal reads it. */
export function clientAccessView(access: ClientPortalAccess | null | undefined, roleId: string | null, clientId: string | null): Record<ClientAccessKey, boolean> {
  return Object.fromEntries(CLIENT_ACCESS_KEYS.map((k) => [k, clientKeyOn(access, roleId, clientId, k)])) as Record<ClientAccessKey, boolean>;
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
/** A collection the client API reads or writes, with the matrix keys the request must hold (all of them). */
export type ClientGate = { collection: string; methods?: Method[]; keys: ClientAccessKey[] };

/** Which collections a client reaches through the generic API, and which matrix rows gate them. A collection with no gate
 *  is not limited by the matrix (the client's own record, its contacts and the catalog stay as they were). */
export const CLIENT_GATES: ClientGate[] = [
  { collection: "projects", keys: ["projects"] },
  { collection: "tasks", keys: ["projects"] },
  { collection: "milestones", keys: ["projects"] },
  { collection: "statusUpdates", keys: ["projects"] },
  { collection: "changeRequests", keys: ["changes"] },
  { collection: "proofs", keys: ["approvals"] },
  { collection: "proofPins", keys: ["approvals"] },
  { collection: "triage", keys: ["requests"] },
  { collection: "invoices", keys: ["money", "seeInvoices"] },
  { collection: "payments", keys: ["money"] },
  { collection: "subscriptions", keys: ["plans"] },
  { collection: "packages", keys: ["plans"] },
  { collection: "services", keys: ["store"] },
  { collection: "orders", keys: ["store"] },
  { collection: "files", keys: ["files"] },
  { collection: "files", methods: ["POST", "PATCH", "PUT", "DELETE"], keys: ["files", "uploadFiles"] },
  { collection: "folders", keys: ["files"] },
  { collection: "forms", keys: ["forms"] },
  { collection: "chatChannels", keys: ["messages"] },
  { collection: "chatMessages", keys: ["messages"] },
  { collection: "tickets", keys: ["tickets"] },
  { collection: "tickets", methods: ["POST"], keys: ["tickets", "createTickets"] },
  { collection: "ticketReplies", keys: ["tickets"] },
  { collection: "meetings", keys: ["calendar"] },
  { collection: "bookings", keys: ["calendar"] },
  { collection: "bookingPages", keys: ["calendar"] },
  { collection: "helpArticles", keys: ["help"] },
  { collection: "helpCategories", keys: ["help"] },
  { collection: "reports", keys: ["reports", "seeReports"] },
  { collection: "dashboards", keys: ["reports", "seeReports"] },
  { collection: "timeEntries", keys: ["seeTimeLogs"] },
  { collection: "metricSamples", keys: ["home"] },
];

/** The matrix keys a client request on one collection and method must hold, or null when no row gates it. */
export function clientGateKeys(collection: string, method: string): ClientAccessKey[] | null {
  const keys = new Set<ClientAccessKey>();
  for (const g of CLIENT_GATES) {
    if (g.collection !== collection) continue;
    if (g.methods && !g.methods.includes(method as Method)) continue;
    for (const k of g.keys) keys.add(k);
  }
  return keys.size ? [...keys] : null;
}

/** The portal path (as the sidebar links it) each section guards. Home is never hidden: a client always lands somewhere. */
const PATH_KEYS: [string, ClientAccessKey][] = [
  ["/portal/projects", "projects"],
  ["/portal/approvals", "approvals"],
  ["/portal/requests", "requests"],
  ["/portal/changes", "changes"],
  ["/portal/briefs", "briefs"],
  ["/portal/social", "social"],
  ["/portal/money", "money"],
  ["/portal/plans", "plans"],
  ["/portal/store", "store"],
  ["/portal/messages", "messages"],
  ["/portal/tickets", "tickets"],
  ["/portal/calendar", "calendar"],
  ["/portal/files", "files"],
  ["/portal/reports", "reports"],
  ["/portal/forms", "forms"],
  ["/portal/help", "help"],
  ["/portal/apps", "apps"],
];

/** The section a portal page belongs to, or null for a page no row hides (home, settings, welcome, sign-in). */
export function clientKeyForPath(path: string): ClientAccessKey | null {
  const hit = PATH_KEYS.find(([p]) => path === p || path.startsWith(`${p}/`));
  return hit ? hit[1] : null;
}
